import { DateTime } from "luxon";

export const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr) return "";

  // Handle date - works with both Date objects and strings
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    console.error("Invalid date format");
    return "";
  }

  // Format date components (already properly padded)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  // Handle time - initialize with default padded values
  let hours = "00";
  let minutes = "00";

  if (timeStr instanceof Date) {
    // Extract from Date object and pad
    hours = String(timeStr.getHours()).padStart(2, "0");
    minutes = String(timeStr.getMinutes()).padStart(2, "0");
  } else if (typeof timeStr === "string") {
    if (timeStr.includes(":")) {
      // Split and pad each component
      const [h, m] = timeStr.split(":");
      hours = h.padStart(2, "0");
      minutes = m.padStart(2, "0");
    } else if (timeStr.match(/\d{1,2}:\d{2}:\d{2}/)) {
      // Handle full time strings
      const timeMatch = timeStr.match(/(\d{1,2}):(\d{2}):(\d{2})/);
      hours = timeMatch[1].padStart(2, "0");
      minutes = timeMatch[2].padStart(2, "0");
    }
  }

  // Final validation (redundant padding for safety)
  hours = Math.min(23, Math.max(0, parseInt(hours, 10)))
    .toString()
    .padStart(2, "0");
  minutes = Math.min(59, Math.max(0, parseInt(minutes, 10)))
    .toString()
    .padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};
export function formatAuctionDateTime(dateString) {
  // Ensure the input date string is properly interpreted as UTC
  // The input format is expected to be like "2025-05-29 22:00:00"

  // JavaScript's Date constructor will interpret this as local time by default
  // To force it to be interpreted as UTC, we need to either:
  // 1. Replace the space with 'T' and append 'Z' to make it ISO format
  // 2. Or use Date.UTC() to explicitly create a UTC time

  // Approach 1: Convert to ISO format
  let utcDateString;
  if (dateString.includes(" ")) {
    // Format: "YYYY-MM-DD HH:MM:SS" -> "YYYY-MM-DDTHH:MM:SSZ"
    utcDateString = dateString.replace(" ", "T") + "Z";
  } else {
    // Already in a format that Date can parse
    utcDateString = dateString;
  }

  // Create a date object - now correctly interpreted as UTC
  const date = new Date(utcDateString);

  // Get the components in local time
  // When accessing date components with these methods, JavaScript
  // automatically converts to the local timezone
  const localYear = date.getFullYear();
  const localMonth = String(date.getMonth() + 1).padStart(2, "0");
  const localDay = String(date.getDate()).padStart(2, "0");
  const localHours = String(date.getHours()).padStart(2, "0");
  const localMinutes = String(date.getMinutes()).padStart(2, "0");
  const localSeconds = String(date.getSeconds()).padStart(2, "0");

  // Return in the desired format
  return `${localYear}/${localMonth}/${localDay} - ${localHours}:${localMinutes}:${localSeconds}`;
}

export function formatTimeFromSeconds(totalSeconds) {
  if (typeof totalSeconds !== "number" || totalSeconds < 0) {
    return "Invalid input: Please provide a non-negative number of seconds.";
  }

  const hours = Math.floor(totalSeconds / 3600);
  const remainingSecondsAfterHours = totalSeconds % 3600;
  const minutes = Math.floor(remainingSecondsAfterHours / 60);
  const seconds = remainingSecondsAfterHours % 60;

  return {
    hours: hours,
    minutes: minutes,
    seconds: seconds,
  };
}

/**
 * Converts date and date-time strings to UTC format
 * Can handle inputs from both formatDateTime ("YYYY-MM-DD HH:MM") and formatDateOnly ("YYYY-MM-DD")
 *
 * @param {string} dateStr - Date string in format "YYYY-MM-DD" or "YYYY-MM-DD HH:MM"
 * @returns {string} - Date string in format "YYYY-MM-DD" or "YYYY-MM-DD HH:MM" in UTC
 */
export const convertToUTCFormat = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") {
    console.error("Invalid input: expected a non-empty string");
    return "";
  }

  // Check if the input is date-only or date-time format
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const isDateTime = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(dateStr);
  const isDateTime2 = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateStr);

  if (!isDateOnly && !isDateTime && !isDateTime2) {
    console.error(
      "Invalid format: expected 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM'"
    );
    return "";
  }

  try {
    // Create a Date object from the input string (which is in local time)
    // For date-only, we'll assume the time is 00:00 local time
    const date = new Date(dateStr.replace(" ", "T"));

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date created from input string");
      return "";
    }

    // Format the date part in UTC
    const formattedDate =
      date.getUTCFullYear() +
      "-" +
      String(date.getUTCMonth() + 1).padStart(2, "0") +
      "-" +
      String(date.getUTCDate()).padStart(2, "0");

    // If it's date-only, return just the date part
    if (isDateOnly) {
      return formattedDate;
    }

    // If it's date-time, also format the time part (without seconds)
    const formattedTime =
      String(date.getUTCHours()).padStart(2, "0") +
      ":" +
      String(date.getUTCMinutes()).padStart(2, "0");

    return `${formattedDate} ${formattedTime}`;
  } catch (error) {
    console.error("Error converting date format:", error);
    return "";
  }
};

export function convertUtcStringFormat(dateTimeString) {
  // Input format: YYYY-MM-DD HH:MM (e.g., "2025-05-31 06:30")
  // Output format: YYYY-MM-DDTHH:MM:SSZ (e.g., "2025-05-31T06:30:00Z")

  // Add seconds and the 'Z' (Zulu time, indicating UTC)
  // The 'T' separator is already handled by simply concatenating parts.
  const parts = dateTimeString?.split?.(' ');
  const datePart = parts[0];
  const timePart = parts[1];

  return `${datePart}T${timePart}Z`;
}

export const convertFromUTCToGivenTimezone = ({ dateStr, timezone }) => {
  let returnVal = "";
  // Parse the UTC time
  const utcDateTime = DateTime.fromISO(dateStr, { zone: "UTC" });

  console.log(utcDateTime, `utcDateTime`)

  // Convert the UTC time to the specified timezone
  const localDateTime = utcDateTime.setZone(timezone);

  // Return the local time in ISO format with the appropriate local timezone offset
  const splittedDate = localDateTime?.toISO()?.split("T");

  console.log(splittedDate, dateStr, "splittedDate")

  const time = splittedDate?.[1]?.split("-")?.[0];

  returnVal = splittedDate?.[0] + "T" + time;

  return returnVal;
};
